import {Component, OnDestroy, OnInit}  from '@angular/core';
import {Observable}                    from 'rxjs';
import {IGridData}                     from '../../../ap-interface';
import {ApDynGridColumnConfigBase}     from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridStatusColumnConfig}   from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {ApDynGridPropertyColumnConfig} from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {SortDescriptor}                from '@progress/kendo-data-query';
import {ApDynGridPagerConfigBase}      from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {ApDynGridEditColumnConfig}     from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApJobsEditComponent
}                                      from '../../../entry-components/ap-administration-entry-components/ap-jobs-edit.component';
import {JobsStore}                     from '../../../stores/administration/jobs.store';
import {filter, map}                   from 'rxjs/operators';
import {FormStore}                     from '../../../stores/layout/form.store';
import {MapViewStore}                  from '../../../stores/layout/mapview.store';
import {LanguageStore}                 from '../../../stores/translation/language.store';
import {TranslationStore}              from '../../../stores/translation/translation.store';
import * as moment                     from 'moment';
import {ApDynGridPagerConfig}          from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import IJobs = Data.Job.IJobs;
import {LoginStore}                    from '../../../stores/login/login.store';

interface IJobsGridData extends IGridData<IJobs> {
  Status: any;
  StatusClass: any;
  StatusDesc: any;
  ScheduledAtFormatted: any;
  ScheduledFormatted: any;
  CreatedAtDate: any;
  CreatedAtFormatted: any;
  StartedAtDate: any;
  StartedAtFormatted: any;
  FinishedAtDate: any;
  FinishedAtFormatted: any;
  LastActivityDate: any;
  LastActivityFormatted: any;
  Duration: any;
  IntervalFormatted: any;
  InputShortened: any;
  OutputShortened: any;
  PriorityText: any;
}

@Component({
  selector: 'ap-jobs',
  template: `
    <ap-dyncomponent [caption]="'Global__Overview_Jobs'"
                     [canSearch]="true"
                     [canCreate]="false"
                     [columns]="columns"
                     [headerIcon]="'ap-basedata-icon'"
                     [items]="items$ | async"
                     [loading$]="loading$"
                     [pager]="pager"
                     (edit)="showDetails($event)"></ap-dyncomponent>`,
})
export class ApJobsComponent implements OnInit, OnDestroy {
  public loading$: Observable<boolean>;
  public subscriptions = [];
  public columns: ApDynGridColumnConfigBase[];
  public items$: Observable<any>;
  public pager: ApDynGridPagerConfigBase;
  public sort: SortDescriptor[];

  /**
   * Constructor
   */
  constructor(private jobsStore: JobsStore,
              private formStore: FormStore,
              private loginStore: LoginStore,
              private mapViewStore: MapViewStore,
              private languageStore: LanguageStore,
              private translationStore: TranslationStore) {
    this.mapViewStore.hideMapView();
    this.jobsStore.loadAllFarmJobs(this.loginStore.SelectedFarmId, 90);
    this.loading$ = this.jobsStore.Listen(s => s.farmJobsLoading);
    this.items$ = this.jobsStore.Listen(s => s.allFarmJobs).pipe(
      filter(d => !!d),
      map(d => d.Convert<IJobsGridData>(job => {
        return {
          Id: job.Id,
          SourceItem: job,
          Status: job.Status,
          StatusClass: job.Status === -1 ? 'ap-status-red' : job.Status === 2 ? 'ap-status-green' : 'ap-status-yellow',
          StatusDesc: ApJobsComponent.extractStatusDesc(job),
          ScheduledAtFormatted: job.Schedule && job.Schedule.ScheduledAt ? job.Schedule.ScheduledAt : '-',
          ScheduledFormatted: job.Schedule && job.Schedule.Id ?
            this.translationStore.FindTranslationForSelectedLanguage('LabelYes') :
            this.translationStore.FindTranslationForSelectedLanguage('LabelNo'),
          CreatedAtDate: job.CreatedAt ? moment(job.CreatedAt, this.languageStore.IsoDateTimeFormat).toDate() : null,
          CreatedAtFormatted: job.CreatedAt ? moment(job.CreatedAt, this.languageStore.IsoDateTimeFormat)
            .format(this.languageStore.CurrentDateTimeFormat) : '-',
          StartedAtDate: job.StartedAt ? moment(job.StartedAt, this.languageStore.IsoDateTimeFormat).toDate() : null,
          StartedAtFormatted:
            job.StartedAt ? moment(job.StartedAt, this.languageStore.IsoDateTimeFormat)
              .format(this.languageStore.CurrentDateTimeFormat) : '-',
          FinishedAtDate:
            job.FinishedAt ? moment(job.FinishedAt, this.languageStore.IsoDateTimeFormat).toDate() : null,
          FinishedAtFormatted:
            job.FinishedAt ? moment(job.FinishedAt, this.languageStore.IsoDateTimeFormat)
              .format(this.languageStore.CurrentDateTimeFormat) : '-',
          LastActivityDate:
            job.LastActivityAt ? moment(job.LastActivityAt, this.languageStore.IsoDateTimeFormat).toDate() : null,
          LastActivityFormatted: job.LastActivityAt ? moment(job.LastActivityAt, this.languageStore.IsoDateTimeFormat)
            .format(this.languageStore.CurrentDateTimeFormat) : '-',
          Duration: ApJobsComponent.extractDuration(job),
          IntervalFormatted: job.Schedule && job.Schedule.Interval ? job.Schedule.Interval : '-',
          InputShortened: job.Input && job.Input.length > 30 ? job.Input.substring(0, 30) + '...' : job.Input,
          OutputShortened: job.Output && job.Output.length > 30 ? job.Output.substring(0, 30) + '...' : job.Output,
          PriorityText: job.Priority ? job.Priority : ''
        };
      })));
  }

  private static extractStatusDesc(job): string {
    if (job.Status === 0) {
      return 'Global__ProcessWaiting';
    } else if (job.Status === 1) {
      return 'Data_RuleResult__Processing';
    } else if (job.Status === 2) {
      return 'Global__ProcessSuccessful';
    } else {
      return 'Global__ProcessFailed';
    }
  }

  private static extractDuration(job): string {
    const diff = moment(job.FinishedAt).diff(moment(job.StartedAt));
    return diff ? moment.utc(diff).format('HH:mm:ss') : '';
  }

  ngOnInit(): void {
    this.buildGrid();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(d => {
      d.unsubscribe();
    });
  }

  buildGrid(): void {
    this.columns = [
      new ApDynGridStatusColumnConfig({
        field: 'Status',
        class: 'StatusClass',
        description: 'StatusDesc',
        showHeaderTitle: false,
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.JobType.Name',
        title: 'Global_Type',
        headerFilterable: true,
        filterable: true,
        width: 120
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'CreatedAtDate',
        title: 'Global__CreatedAt',
        headerFilterable: true,
        width: 150,
        format: this.languageStore.CurrentDateTimeFormat,
        sortIndex: 0,
        sortDesc: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'StartedAtDate',
        title: 'Global_Started_At',
        headerFilterable: true,
        format: this.languageStore.CurrentDateTimeFormat,
        width: 150
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'LastActivityDate',
        title: 'Global_LastActive_At',
        headerFilterable: true,
        format: this.languageStore.CurrentDateTimeFormat,
        width: 150
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'FinishedAtDate',
        title: 'Global__Finished_At',
        headerFilterable: true,
        format: this.languageStore.CurrentDateTimeFormat,
        width: 150
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Duration',
        title: 'Docu_Ini__Duration',
        headerFilterable: true,
        width: 90
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'ScheduledFormatted',
        title: 'Global_Schedule',
        headerFilterable: true,
        width: 90
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'PriorityText',
        title: 'Global_Priority',
        headerFilterable: true,
        filterable: true,
        width: 80
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'InputShortened',
        headerFilterable: true,
        title: 'Global_Input',
        hide: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'OutputShortened',
        headerFilterable: true,
        title: 'Global_Output',
        hide: true
      }),
      new ApDynGridEditColumnConfig({})
    ];
    this.pager = new ApDynGridPagerConfig({});
  }

  /**
   * Handler for showing job details
   * User wants to see details
   */
  public showDetails(dataItem): void {
    this.formStore.openForm({component: ApJobsEditComponent, data: dataItem});
  }
}
